// A Price Grid's cells as an editable grid (priceGrids.mjs): a row for every
// Company Size, and on a Company Size and Quantity grid for every quantity on
// its menu, so a whole menu is filled in at once. A row with no amount is an
// option not offered; Save sends the rows that have one. Pure: no fetching.

export interface GridDimension {
  object: string;
  field: string;
  label: string;
  values?: { key: string; label: string; value?: number }[];
}

export interface PriceGridPayload {
  grid: {
    id: string;
    name: string;
    prices_by: 'company_size' | 'company_size_and_quantity' | null;
    quantity_label: string | null;
    dimensions: GridDimension[];
    active: boolean;
  };
  sizes: { key: string; label: string }[];
  cells: { id: string; key_1: string; key_2: string | null; amount: number; hours: number; label: string | null }[];
}

export interface DraftCell {
  /** Stable row id: its values. */
  key: string;
  key_1: string;
  key_2: string | null;
  /** What the values are called on the row. */
  first: string;
  second: string | null;
  amount: number | null;
  hours: number | null;
  label: string;
}

export interface WireCell {
  key_1: string;
  key_2: string | null;
  amount: number;
  hours: number;
  label: string | null;
}

const rowKey = (key1: string, key2: string | null) => `${key1}|${key2 ?? ''}`;
const isSize = (d: GridDimension | undefined) => d?.object === 'account' && d.field === 'company_size';
const byNumber = (a: string, b: string) => Number(a) - Number(b) || a.localeCompare(b);

/** A dimension value's label: a Company Size's, the grid's own, or the key. */
function labelOf(payload: PriceGridPayload, index: number, key: string): string {
  const d = payload.grid.dimensions[index];
  if (isSize(d)) return payload.sizes.find((s) => s.key === key)?.label ?? key;
  return d?.values?.find((v) => v.key === key)?.label ?? key;
}

/** The menu's quantities: those its dimension lists and its cells name, in order. */
export function quantitiesOf(payload: PriceGridPayload): string[] {
  const listed = (payload.grid.dimensions[1]?.values ?? []).map((v) => v.key);
  return [...new Set([...listed, ...payload.cells.map((c) => c.key_2).filter((k): k is string => k !== null)])].sort(byNumber);
}

function row(payload: PriceGridPayload, key1: string, key2: string | null): DraftCell {
  const cell = payload.cells.find((c) => c.key_1 === key1 && c.key_2 === key2);
  return {
    key: rowKey(key1, key2),
    key_1: key1,
    key_2: key2,
    first: labelOf(payload, 0, key1),
    second: key2 === null ? null : labelOf(payload, 1, key2),
    amount: cell?.amount ?? null,
    hours: cell?.hours ?? null,
    label: cell?.label ?? '',
  };
}

/**
 * The grid's rows: every Company Size (by quantity, on a quantity grid), each
 * with its cell's amount, hours and label when it has one. A grid with its
 * own dimensions shows the cells it has.
 */
export function toDraft(payload: PriceGridPayload): DraftCell[] {
  const sizes = payload.sizes.map((s) => s.key);
  if (payload.grid.prices_by === 'company_size') return sizes.map((size) => row(payload, size, null));
  if (payload.grid.prices_by === 'company_size_and_quantity') {
    return quantitiesOf(payload).flatMap((q) => sizes.map((size) => row(payload, size, q)));
  }
  return payload.cells.map((c) => row(payload, c.key_1, c.key_2));
}

/** Adds a quantity to the menu: a row for it at every Company Size, unless it is there. */
export function addQuantity(draft: DraftCell[], payload: PriceGridPayload, quantity: number): DraftCell[] {
  const key2 = String(quantity);
  if (draft.some((r) => r.key_2 === key2)) return draft;
  const label = payload.grid.quantity_label ?? 'Quantity';
  const added = payload.sizes.map((s) => ({ ...row(payload, s.key, key2), second: `${key2} ${label}` }));
  return [...draft, ...added].sort((a, b) => byNumber(a.key_2 ?? '', b.key_2 ?? '') || payload.sizes.findIndex((s) => s.key === a.key_1) - payload.sizes.findIndex((s) => s.key === b.key_1));
}

/** Sets one field of one row from an inline editor's text. */
export function setCell(draft: DraftCell[], key: string, field: 'amount' | 'hours' | 'label', value: string | null): DraftCell[] {
  return draft.map((r) => {
    if (r.key !== key) return r;
    if (field === 'label') return { ...r, label: value ?? '' };
    const n = value === null || value.trim() === '' ? null : Number(value);
    return { ...r, [field]: n === null || Number.isNaN(n) ? null : n };
  });
}

/** What Save sends: the rows with an amount (hours default to 0, a blank label to none). */
export function toWire(draft: DraftCell[]): WireCell[] {
  return draft
    .filter((r) => r.amount !== null)
    .map((r) => ({ key_1: r.key_1, key_2: r.key_2, amount: r.amount as number, hours: r.hours ?? 0, label: r.label.trim() || null }));
}

/** Whether the draft differs from what was saved. */
export function isDirty(draft: DraftCell[], payload: PriceGridPayload): boolean {
  const saved = payload.cells.map((c) => ({ key_1: c.key_1, key_2: c.key_2, amount: c.amount, hours: c.hours, label: c.label }));
  const order = (cells: WireCell[]) => JSON.stringify([...cells].sort((a, b) => rowKey(a.key_1, a.key_2).localeCompare(rowKey(b.key_1, b.key_2))));
  return order(toWire(draft)) !== order(saved);
}

/** Rows that cannot be saved as they are: hours or a label with no amount, or a negative figure. */
export function problems(draft: DraftCell[]): string[] {
  const name = (r: DraftCell) => [r.second, r.first].filter(Boolean).join(' · ');
  return draft.flatMap((r) => {
    if (r.amount === null && (r.hours !== null || r.label.trim() !== '')) return [`Give ${name(r)} an amount, or clear its hours and label.`];
    if ((r.amount ?? 0) < 0 || (r.hours ?? 0) < 0) return [`${name(r)} cannot be negative.`];
    return [];
  });
}
